// AttendancePage.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { FaPlus } from "react-icons/fa";
import AddEditAttendancePage from "./AddEditAttendancePage";
import { axiosMaster, axiosSalary } from "../../Components/api/axiosInstance";

const AttendancePage = () => {
  const [groups, setGroups] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedRest, setSelectedRest] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState(null);

  const [presentToday, setPresentToday] = useState([]);
  const [unprocessedLogs, setUnprocessedLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUnprocessed, setLoadingUnprocessed] = useState(false);

  const [formOpen, setFormOpen] = useState(false);

  /* ================= GROUPS ================= */
  const fetchGroups = async () => {
    try {
      const res = await axiosMaster.get("/GroupMaster");
      setGroups((res.data || []).map((g) => ({ value: g.groupId, label: g.groupName })));
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= RESTAURANTS ================= */
  const fetchRestaurants = async (groupId) => {
    if (!groupId) return;
    try {
      const res = await axiosMaster.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`);
      setRestaurants((res.data || []).map((r) => ({ value: r.restId, label: r.restaurantName })));
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= EMPLOYEES ================= */
  const fetchEmployees = async (restId) => {
    if (!restId) return;
    try {
      const res = await axiosMaster.get(`/EmployeeMaster/GetByRestaurant?restId=${restId}`);
      setEmployees((res.data || []).map((e) => ({ value: e.empId, label: e.empName })));
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= PRESENT TODAY ================= */
  const fetchPresentToday = async (groupId) => {
    if (!groupId) return;
    try {
      setLoading(true);
      const res = await axiosSalary.get(
        `/AttendanceLog/GetPresentTodayByGroup?groupId=${groupId}`
      );
      setPresentToday(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= UNPROCESSED LOGS ================= */
  const fetchUnprocessedLogs = async (groupId, restaurantId) => {
    if (!groupId || !restaurantId) {
      setUnprocessedLogs([]);
      return;
    }
    try {
      setLoadingUnprocessed(true);
      const res = await axiosSalary.get(
        `/AttendanceLog/GetUnProcessed?groupId=${groupId}&restaurantId=${restaurantId}`
      );
      setUnprocessedLogs(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUnprocessed(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    setSelectedRest(null);
    setSelectedEmp(null);
    setRestaurants([]);
    setEmployees([]);
    setUnprocessedLogs([]);
    fetchRestaurants(selectedGroup?.value);
    fetchPresentToday(selectedGroup?.value);
  }, [selectedGroup]);

  useEffect(() => {
    setSelectedEmp(null);
    setEmployees([]);
    fetchEmployees(selectedRest?.value);
    fetchUnprocessedLogs(selectedGroup?.value, selectedRest?.value);
  }, [selectedRest]);

  /* ================= HELPERS ================= */
  const getEmpName = (empId) =>
    employees.find((e) => e.value === empId)?.label || "-";

  /* ================= ADD PUNCH ================= */
  const openAdd = () => {
    if (!selectedGroup || !selectedRest || !selectedEmp) {
      alert("Select group, restaurant and employee first");
      return;
    }
    setFormOpen(true);
  };

  const handlePunchSuccess = () => {
    setFormOpen(false);
    fetchPresentToday(selectedGroup?.value);
    fetchUnprocessedLogs(selectedGroup?.value, selectedRest?.value);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-5 flex flex-col h-[calc(100vh-120px)] gap-5">
      <div className="flex flex-col md:flex-row justify-between gap-3">
        <h2 className="text-xl font-bold">Attendance</h2>

        <div className="flex flex-col md:flex-row gap-2 md:items-center flex-wrap">
          <div className="w-full md:w-56">
            <Select options={groups} value={selectedGroup} onChange={setSelectedGroup} placeholder="Select Group" />
          </div>
          <div className="w-full md:w-56">
            <Select
              options={restaurants}
              value={selectedRest}
              onChange={setSelectedRest}
              placeholder="Select Restaurant"
              isDisabled={!selectedGroup}
            />
          </div>
          <div className="w-full md:w-56">
            <Select
              options={employees}
              value={selectedEmp}
              onChange={setSelectedEmp}
              placeholder="Select Employee"
              isDisabled={!selectedRest}
            />
          </div>

          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg"
          >
            <FaPlus /> Punch
          </button>
        </div>
      </div>

      {/* PRESENT TODAY */}
      <div className="flex-1 min-h-0 flex flex-col">
        <h3 className="font-semibold text-slate-700 mb-2">Present Today</h3>
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="border p-2">Emp Id</th>
                <th className="border p-2">Employee</th>
                <th className="border p-2">Rest Id</th>
                <th className="border p-2">First Punch</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center p-5">
                    Loading...
                  </td>
                </tr>
              ) : presentToday.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center p-5">
                    No Data Found
                  </td>
                </tr>
              ) : (
                presentToday.map((row) => (
                  <tr key={`${row.empId}-${row.restId}`} className="hover:bg-slate-50">
                    <td className="border p-2">{row.empId}</td>
                    <td className="border p-2">{row.empName || "-"}</td>
                    <td className="border p-2">{row.restId}</td>
                    <td className="border p-2">{row.firstPunchDisplayTime}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* UNPROCESSED LOGS */}
      <div className="flex-1 min-h-0 flex flex-col">
        <h3 className="font-semibold text-slate-700 mb-2">Unprocessed Logs</h3>
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="border p-2">Emp Id</th>
                <th className="border p-2">Employee</th>
                <th className="border p-2">Punch Time</th>
                <th className="border p-2">Device</th>
                <th className="border p-2">Entry Date</th>
              </tr>
            </thead>
            <tbody>
              {!selectedRest ? (
                <tr>
                  <td colSpan="5" className="text-center p-5">
                    Select a restaurant to view unprocessed logs
                  </td>
                </tr>
              ) : loadingUnprocessed ? (
                <tr>
                  <td colSpan="5" className="text-center p-5">
                    Loading...
                  </td>
                </tr>
              ) : unprocessedLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center p-5">
                    No Data Found
                  </td>
                </tr>
              ) : (
                unprocessedLogs.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="border p-2">{row.empId}</td>
                    <td className="border p-2">{getEmpName(row.empId)}</td>
                    <td className="border p-2">
                      {new Date(row.punchTime).toLocaleString()}
                    </td>
                    <td className="border p-2">{row.deviceId}</td>
                    <td className="border p-2">{row.entryDate}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <AddEditAttendancePage
          groupId={selectedGroup?.value}
          restId={selectedRest?.value}
          empId={selectedEmp?.value}
          onClose={() => setFormOpen(false)}
          onSuccess={handlePunchSuccess}
        />
      )}
    </div>
  );
};

export default AttendancePage;